/*
  Welcome offer — sections/welcome-offer.liquid.

  Two independent jobs. The section renders the markup for exactly one of
  them per page view, so only one half of this file ever does anything.

  1. SIGNED OUT — the header "Sign in or create account" panel
     Shopify's <shopify-account> panel is shadow DOM. This clones the offer
     block from the section's <template> into it, moves the email field
     above "Sign in with Shop", and hides the "Email me with news and
     offers" checkbox.

     The checkbox is hidden with CSS only. It is never ticked or unticked
     from here: whatever default Shopify gives it is what gets submitted.

     FRAGILITY, STATED PLAINLY
     Same caveat as assets/shop-login-skin.js: this is unsupported work
     inside Shopify's own component. Every step looks its target up and
     gives up quietly if it is not there, so a Shopify restructure leaves
     the panel exactly as Shopify ships it — checkbox back, no offer —
     rather than broken. Hooks used: .account__content, .account__login-slot,
     .login-form__root, .login-form__email-form, .login-form__divider,
     .login-form__marketing-consent.

  2. SIGNED IN, NO ORDERS — the code dialog
     Opens once per browser, after the cookie banner has been answered and
     with no drawer open. The page is not scroll-locked while it is open:
     this theme's drawer lock (touch-action: none on <body>) is known to
     freeze scrolling inside the overlay itself on phones.
*/
(function () {
  'use strict';

  /* ------------------------------------------------------------------
     1. Sign-in panel
     ------------------------------------------------------------------ */
  var template = document.querySelector('template[data-welcome-offer-panel]');

  if (template) {
    var STYLE_MARK = 'data-vwo-style';

    /* Hardcoded colours for the same reason shop-login-skin.js does it:
       the panel is white in both site themes, so the light/dark tokens do
       not apply inside it. Font variables do inherit across the boundary. */
    var ACCOUNT_CSS = [
      '.vwo-panel{border-top:2px solid #e1a03e;padding-top:14px;text-align:start;color:#0d0d0d}',
      '.vwo-panel p{margin:0}',
      '.vwo-panel__eyebrow{font-family:var(--font-headline,inherit);font-size:12px;line-height:1.4;' +
        'letter-spacing:.18em;text-transform:uppercase}',
      '.vwo-panel__heading{font-family:var(--font-headline,inherit);font-size:40px;line-height:1.05;' +
        'letter-spacing:.02em;margin-top:10px!important}',
      '.vwo-panel__heading::after{content:"";display:block;width:90px;height:3px;margin-top:10px;' +
        'background:linear-gradient(90deg,#e1a03e,#ebd27b)}',
      '.vwo-panel__text{font-family:var(--font-base,inherit);font-size:14px;line-height:1.5;color:#555;' +
        'margin-top:10px!important}',
      /* After the offer: the account's benefits in the same slot. */
      '.vwo-panel--account .vwo-panel__heading{font-size:30px;line-height:1.1;letter-spacing:-.01em}',
      '.vwo-panel--account .vwo-panel__heading::after{width:64px;margin-top:12px}',
      '.vwo-panel__perks{list-style:none;margin:16px 0 0;padding:0;display:flex;flex-direction:column;gap:12px}',
      '.vwo-panel__perk{display:flex;gap:12px;align-items:flex-start}',
      '.vwo-panel__perk-icon{flex:none;width:36px;height:36px;border-radius:50%;background:#0d0d0d;' +
        'display:flex;align-items:center;justify-content:center}',
      '.vwo-panel__perk-icon svg{width:18px;height:18px;fill:none;stroke:#ebd27b;stroke-width:1.7;' +
        'stroke-linecap:round;stroke-linejoin:round}',
      '.vwo-panel__perk-body{display:flex;flex-direction:column;gap:2px;padding-top:1px;' +
        'font-family:var(--font-base,inherit)}',
      '.vwo-panel__perk-title{font-size:14px;font-weight:700;line-height:1.35}',
      '.vwo-panel__perk-text{font-size:13px;line-height:1.4;color:#555}',
      /* Arabic is a joined script: tracking pulls the letters apart. Read
         from <html> here because :host-context() is Chromium-only. */
      document.documentElement.dir === 'rtl'
        ? '.vwo-panel__eyebrow,.vwo-panel__heading{letter-spacing:0}' +
          '.vwo-panel__heading::after{background:linear-gradient(270deg,#e1a03e,#ebd27b)}'
        : '',
    ].join('');

    /* .login-form__root is already a flex column, so `order` is enough to
       put the email field first without moving any of Shopify's nodes. */
    var LOGIN_CSS = [
      '.login-form__marketing-consent{display:none!important}',
      '.login-form__email-form{order:1}',
      '.login-form__divider{order:2}',
      '::slotted([slot="shop-login"]){order:3}',
      '.login-form__email-input{border-color:#0d0d0d!important}',
    ].join('');

    var watchedRoots = [];
    var queued = false;

    function addStyle(root, css) {
      if (root.querySelector('style[' + STYLE_MARK + ']')) return;
      var style = document.createElement('style');
      style.setAttribute(STYLE_MARK, '');
      style.textContent = css;
      root.appendChild(style);
    }

    /* Mutations inside a shadow root do not reach an observer on the light
       DOM, and Shopify rebuilds the panel's contents when it opens. */
    function watchRoot(root) {
      if (watchedRoots.indexOf(root) !== -1) return;
      watchedRoots.push(root);
      new MutationObserver(schedule).observe(root, { childList: true, subtree: true });
    }

    function apply() {
      queued = false;

      var hosts = document.querySelectorAll('shopify-account');

      for (var i = 0; i < hosts.length; i++) {
        var root = hosts[i].shadowRoot;
        if (!root) continue;
        watchRoot(root);

        var content = root.querySelector('.account__content');
        var slot = content && content.querySelector('.account__login-slot');
        var loginForm = slot && slot.querySelector('shopify-login-form');
        var existing = root.querySelector('[data-vwo-panel-offer]');

        /* No login form means the signed-in menu (or a layout this file
           does not recognise): the offer has no business there. */
        if (!loginForm) {
          if (existing) existing.remove();
          continue;
        }

        addStyle(root, ACCOUNT_CSS);

        if (!existing) {
          content.insertBefore(template.content.cloneNode(true), slot);
        }

        if (loginForm.shadowRoot) {
          watchRoot(loginForm.shadowRoot);
          addStyle(loginForm.shadowRoot, LOGIN_CSS);
        }
      }
    }

    function schedule() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(apply);
    }

    apply();

    /* <shopify-account> and its login form upgrade well after
       DOMContentLoaded, and the form mounts on the click that opens the
       panel. */
    new MutationObserver(schedule).observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
    document.addEventListener('click', schedule, true);
  }

  /* ------------------------------------------------------------------
     2. Code dialog
     ------------------------------------------------------------------ */
  var root = document.querySelector('[data-welcome-offer]');
  if (!root) return;

  var STATE_KEY = 'veaudry-welcome-offer';
  var panel = root.querySelector('[data-vwo-panel]');
  var isOpen = false;
  var lastFocus = null;

  function read(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function write(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {}
  }

  /* Same measured test as assets/weekly-updates-bar.js: offsetParent is
     always null on a position: fixed element, so it cannot be used here. */
  function consentBannerVisible() {
    var banner = document.querySelector('#shopify-pc__banner, .shopify-pc__banner');
    if (!banner) return false;

    var style = window.getComputedStyle(banner);
    if (style.display === 'none' || style.visibility === 'hidden') return false;

    var box = banner.getBoundingClientRect();
    return box.height > 0 && box.width > 0;
  }

  function overlayOpen() {
    return /(^|\s)page-overlay/.test(document.body.className);
  }

  function focusables() {
    return Array.prototype.filter.call(panel.querySelectorAll('a[href], button:not([disabled])'), function (el) {
      return el.getClientRects().length > 0;
    });
  }

  function onKeydown(event) {
    if (!isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key !== 'Tab') return;

    var items = focusables();
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;

    if (event.shiftKey && (active === first || active === panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function open() {
    if (isOpen) return;
    isOpen = true;
    lastFocus = document.activeElement;

    root.hidden = false;
    /* Two frames so the browser paints the closed state before the class
       lands; one is not enough after toggling `hidden`. */
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        root.classList.add('is-open');
      });
    });

    panel.focus({ preventScroll: true });
    document.addEventListener('keydown', onKeydown);

    /* Recorded on open, not on close: a visitor who navigates away with
       the dialog still up has seen the code and should not get it again. */
    write(STATE_KEY, 'shown');
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;

    root.classList.remove('is-open');
    document.removeEventListener('keydown', onKeydown);

    window.setTimeout(function () {
      if (!isOpen) root.hidden = true;
    }, 400);

    if (lastFocus && typeof lastFocus.focus === 'function') {
      lastFocus.focus({ preventScroll: true });
    }
  }

  function openWhenClear() {
    if (!consentBannerVisible() && !overlayOpen()) {
      open();
      return;
    }

    /* Poll rather than observe: the banner is removed by Shopify's own
       script and does not always mutate an element the theme can watch. */
    var tries = 0;
    var poll = window.setInterval(function () {
      tries += 1;
      if (!consentBannerVisible() && !overlayOpen()) {
        window.clearInterval(poll);
        open();
      } else if (tries > 180) {
        window.clearInterval(poll);
      }
    }, 1000);
  }

  function copyCode(button) {
    var code = button.getAttribute('data-vwo-copy');

    function done() {
      button.classList.add('is-copied');
      var status = root.querySelector('[data-vwo-copied-status]');
      if (status) status.textContent = status.getAttribute('data-copied-text') || '';
      window.setTimeout(function () {
        button.classList.remove('is-copied');
        if (status) status.textContent = '';
      }, 2500);
    }

    function fallback() {
      var field = document.createElement('textarea');
      field.value = code;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      root.appendChild(field);
      field.select();
      try {
        if (document.execCommand('copy')) done();
      } catch (e) {}
      root.removeChild(field);
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done, fallback);
    } else {
      fallback();
    }
  }

  root.addEventListener('click', function (event) {
    if (event.target.closest('[data-vwo-close]')) {
      close();
      return;
    }

    var copy = event.target.closest('[data-vwo-copy]');
    if (copy) copyCode(copy);
  });

  /* Theme editor: never auto-open over the merchant's work, but show the
     dialog when its section is selected so it can be edited in place. */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:select', function (event) {
      if (event.target.contains(root)) open();
    });
    document.addEventListener('shopify:section:deselect', function (event) {
      if (event.target.contains(root)) close();
    });
    return;
  }

  if (read(STATE_KEY) === 'shown') return;

  /* Not at script start: the cookie banner mounts a beat after load, and
     checking before it exists would open the dialog underneath it. */
  window.setTimeout(openWhenClear, 1500);
})();
